import React, { useState, useRef } from "react";

export default function DraggableImageGrid({
  items = [],
  onReorder,
  onDelete,
}) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const touchStartIndex = useRef(null);

  // Mouse Drag Handlers (HTML5)
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try {
        e.dataTransfer.setData("text/plain", String(index));
      } catch (err) {
        // ignore
      }
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "move";
    }
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const currentList = (Array.isArray(items) ? items : items && typeof items === "object" ? Array.from(items) : []).filter(Boolean);
    const newItems = [...currentList];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);
    if (onReorder) onReorder(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch Handlers for Mobile (Hold & Drag)
  const handleTouchStart = (e, index) => {
    touchStartIndex.current = index;
    setDraggedIndex(index);
  };

  const handleTouchMove = (e) => {
    if (touchStartIndex.current === null) return;
    const touch = e.touches[0];
    const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
    if (targetElement) {
      const dropZone = targetElement.closest("[data-drag-index]");
      if (dropZone) {
        const overIndex = parseInt(dropZone.getAttribute("data-drag-index"), 10);
        if (!isNaN(overIndex) && overIndex !== dragOverIndex) {
          setDragOverIndex(overIndex);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchStartIndex.current !== null && dragOverIndex !== null && touchStartIndex.current !== dragOverIndex) {
      const currentList = (Array.isArray(items) ? items : items && typeof items === "object" ? Array.from(items) : []).filter(Boolean);
      const newItems = [...currentList];
      const [draggedItem] = newItems.splice(touchStartIndex.current, 1);
      newItems.splice(dragOverIndex, 0, draggedItem);
      if (onReorder) onReorder(newItems);
    }
    touchStartIndex.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const validItems = (Array.isArray(items) ? items : items && typeof items === "object" ? Array.from(items) : []).filter(Boolean);
  if (validItems.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        marginTop: "12px",
        marginBottom: "12px",
        userSelect: "none",
        touchAction: "pan-y",
      }}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {validItems.map((item, idx) => {
        if (!item) return null;
        const isDragging = draggedIndex === idx;
        const isOver = dragOverIndex === idx;

        let src = "";
        let isNew = false;

        if (typeof item === "string") {
          src = item;
        } else if (item instanceof File) {
          src = URL.createObjectURL(item);
          isNew = true;
        } else if (item && typeof item === "object") {
          if (item.file instanceof File) {
            src = URL.createObjectURL(item.file);
            isNew = true;
          } else {
            src = item.url || item.src || "";
            isNew = !!item.isNew;
          }
        }

        if (!src) return null;

        const key = (item && item.id) || (item && item.name ? `${item.name}-${idx}` : (typeof item === "string" ? `${item}-${idx}` : idx));

        return (
          <div
            key={key}
            data-drag-index={idx}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, idx)}
            style={{
              position: "relative",
              width: "80px",
              height: "80px",
              borderRadius: "8px",
              border: isOver
                ? "2px dashed #1976d2"
                : isNew
                ? "2px dashed #4caf50"
                : "1px solid #e0e0e0",
              boxShadow: isDragging
                ? "0 6px 16px rgba(0,0,0,0.3)"
                : "0 2px 6px rgba(0,0,0,0.1)",
              opacity: isDragging ? 0.4 : 1,
              transform: isDragging ? "scale(1.08)" : isOver ? "scale(0.92)" : "scale(1)",
              transition: "transform 0.15s ease, opacity 0.15s ease, border 0.15s ease",
              cursor: "grab",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={src}
              alt={`Thumbnail ${idx + 1}`}
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
                pointerEvents: "none",
              }}
            />
            {/* Order index badge */}
            <div
              style={{
                position: "absolute",
                bottom: 3,
                left: 3,
                backgroundColor: "rgba(0, 0, 0, 0.65)",
                color: "#ffffff",
                borderRadius: "4px",
                fontSize: "10px",
                padding: "1px 5px",
                pointerEvents: "none",
                fontWeight: "bold",
                lineHeight: "13px",
              }}
            >
              #{idx + 1}
            </div>
            {/* Delete button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(idx, item);
              }}
              title="Delete image"
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                background: "#d32f2f",
                color: "#ffffff",
                border: "2px solid #ffffff",
                borderRadius: "50%",
                width: "22px",
                height: "22px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                zIndex: 10,
              }}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}
